import React from 'react';
import { Lock } from 'lucide-react';
import { CategoryDefinition } from '../../config/categoryConfig';
import { cn } from '../../utils/cn';

interface StartCategoryButtonProps {
  category: CategoryDefinition;
  isLocked: boolean;
  isHovered?: boolean;
  isSelected?: boolean;
  onClick: () => void;
  className?: string;
  variant?: 'circular' | 'grid';
  style?: React.CSSProperties;
}

const StartCategoryButton = React.memo(function StartCategoryButton({
  category,
  isLocked,
  isHovered = false,
  isSelected = false,
  onClick,
  className = '',
  variant = 'grid',
  style,
}: StartCategoryButtonProps) {
  const textColor = category.textColor || getAccessibleTextColor(category.color);
  const isLightText = textColor === '#ffffff';

  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'relative flex flex-col items-center justify-center gap-2 text-center',
        'w-full rounded-2xl px-4 py-5 border',
        'transition-transform duration-300 ease-out',
        'shadow-lg focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-offset-2',
        'focus-visible:ring-offset-white dark:focus-visible:ring-offset-gray-900',
        'min-h-[112px]', // enough room for icon + up to two lines of text
        isHovered && 'scale-[1.04] -translate-y-1 shadow-2xl',
        isSelected && 'ring-2 ring-white/80 ring-offset-2 ring-offset-white dark:ring-offset-gray-900',
        variant === 'grid' ? 'text-sm sm:text-base' : 'text-sm',
        isLightText ? 'focus-visible:ring-white/60' : 'focus-visible:ring-gray-900/50',
        isLocked && 'opacity-90',
        className
      )}
      style={{
        backgroundColor: category.color,
        borderColor: `${category.color}99`,
        boxShadow: `0 8px 16px ${category.color}40`,
        color: textColor,
        willChange: isHovered ? 'transform' : 'auto',
        ...style, // Merge with passed style
      }}
      aria-label={`${category.displayName}${isLocked ? ' (Premium)' : ''}`}
      aria-pressed={isSelected}
      aria-disabled={false}
    >
      {/* Lock badge for premium categories */}
      {isLocked && (
        <span
          className="absolute top-3 right-3 inline-flex items-center gap-1 rounded-full bg-black/40 px-2 py-1 text-[10px] font-semibold uppercase tracking-wide text-white backdrop-blur"
          aria-label="Premium category locked"
        >
          <Lock className="h-3 w-3" />
          Premium
        </span>
      )}

      {/* Category icon */}
      <span className="text-3xl drop-shadow-sm" role="img" aria-label={category.displayName}>
        {category.icon}
      </span>

      {/* Category name */}
      <span className="block font-semibold leading-tight whitespace-normal">
        {category.displayName}
      </span>
    </button>
  );
});

export default StartCategoryButton;

/**
 * Returns a contrasting text color for a given solid background color.
 * Defaults to white if the color is invalid.
 */
function getAccessibleTextColor(hexColor: string): string {
  const hex = hexColor?.replace('#', '');
  if (!hex || (hex.length !== 6 && hex.length !== 3)) {
    return '#ffffff';
  }

  const normalized = hex.length === 3
    ? hex.split('').map(char => char + char).join('')
    : hex;

  const r = parseInt(normalized.slice(0, 2), 16);
  const g = parseInt(normalized.slice(2, 4), 16);
  const b = parseInt(normalized.slice(4, 6), 16);

  if (Number.isNaN(r) || Number.isNaN(g) || Number.isNaN(b)) {
    return '#ffffff';
  }

  // Perceived luminance formula (YIQ)
  const brightness = (r * 299 + g * 587 + b * 114) / 1000;
  return brightness >= 160 ? '#111827' : '#ffffff';
}
